import { TokensResponseDto } from '@lucidlink/dto/web-service-api';
import { assertCanHandle, isNil, resolveAfter, Resolver, } from '@lucidlink/tscore';
import { HttpServerError, NetworkConnectionError, parserFromDto, UnauthorizedError, } from '#fetcher';
import { getServiceAccountWebServiceToken } from '#utils';
import { TokenPair } from './token-pair';
export class InvalidatedTokenError extends Error {
    constructor() {
        super('Token was invalidated');
    }
}
export class JwtTokenManager {
    getUnauthenticatedFetcher;
    tokenStorage;
    tokenKey;
    tokenEndpoint;
    freshTokenResolver;
    isTokenInvalidated = false;
    constructor(params) {
        this.getUnauthenticatedFetcher = params.getUnauthenticatedFetcher;
        this.tokenStorage = params.tokenStorage;
        this.tokenKey = params.tokenName;
        this.tokenEndpoint = params.tokenEndpoint;
        this.freshTokenResolver = new Resolver();
    }
    static async createWithTokens(params) {
        const tokenManager = new JwtTokenManager(params);
        await tokenManager.storeTokens(params.tokens);
        await tokenManager.resolveWithCurrentToken();
        return tokenManager;
    }
    static async tryCreateFromStorage(params) {
        const tokenManager = new JwtTokenManager(params);
        const storedTokens = await tokenManager.getStoredTokens();
        if (isNil(storedTokens)) {
            return null;
        }
        await tokenManager.resolveWithCurrentToken();
        return tokenManager;
    }
    async getFreshToken(forceRefresh) {
        const currentTokenPair = await this.getCurrentTokenPair();
        if (this.isTokenInvalidated || !currentTokenPair) {
            return this.freshTokenResolver.promise;
        }
        if (forceRefresh || currentTokenPair.hasExpired) {
            this.triggerTokenRefresh();
            return this.freshTokenResolver.promise;
        }
        if (currentTokenPair.isAboutToExpire) {
            this.triggerTokenRefresh();
            return Promise.resolve(currentTokenPair.tokens.accessToken);
        }
        return this.freshTokenResolver.promise;
    }
    async invalidateToken() {
        const currentTokenPair = await this.getCurrentTokenPair();
        if (!currentTokenPair) {
            return;
        }
        await this.tokenStorage.remove(this.tokenKey);
        try {
            await this.getUnauthenticatedFetcher().put([this.tokenEndpoint, 'invalidate'], {
                body: {
                    accessToken: currentTokenPair.tokens.accessToken,
                },
            });
        }
        catch (error) {
            if (error instanceof InvalidatedTokenError ||
                error instanceof NetworkConnectionError ||
                error instanceof HttpServerError) {
                return;
            }
            throw error;
        }
    }
    storeTokens(tokens) {
        const storedTokens = tokens.toRawTokens();
        return this.tokenStorage.put(this.tokenKey, storedTokens);
    }
    async getStoredTokens() {
        const storedTokens = await this.tokenStorage.get(this.tokenKey);
        if (isNil(storedTokens)) {
            return undefined;
        }
        return TokenPair.fromStorage(storedTokens);
    }
    async getCurrentTokenPair() {
        const tokens = await this.getStoredTokens();
        if (isNil(tokens)) {
            await this.rejectWithInvalidatedToken();
        }
        return tokens;
    }
    async rejectWithInvalidatedToken() {
        this.isTokenInvalidated = true;
        if (!this.isTokenRefreshInProgress()) {
            this.freshTokenResolver = new Resolver();
        }
        this.freshTokenResolver.reject(new InvalidatedTokenError());
        try {
            /**
             * We unpack the promise which we expect to throw in order to
             * prevent leaving a hanging promise
             */
            await this.freshTokenResolver.promise;
        }
        catch (error) {
            assertCanHandle(error, (err) => err instanceof InvalidatedTokenError);
        }
    }
    async resolveWithCurrentToken() {
        const currentTokens = await this.getCurrentTokenPair();
        if (currentTokens) {
            this.resolveWithTokens(currentTokens);
        }
    }
    resolveWithTokens(tokenPair) {
        if (!this.freshTokenResolver.isCompleted) {
            this.freshTokenResolver.resolve(tokenPair.tokens.accessToken);
        }
    }
    triggerTokenRefresh() {
        if (this.isTokenRefreshInProgress()) {
            return;
        }
        this.freshTokenResolver = new Resolver();
        // Store in case the context changes
        const resolver = this.freshTokenResolver;
        void this.doRefreshToken().catch((err) => {
            if (!resolver.isCompleted) {
                resolver.reject(err);
            }
        });
    }
    async doRefreshToken(retries = 0) {
        const currentTokens = await this.getCurrentTokenPair();
        if (!currentTokens) {
            return;
        }
        try {
            const tokensResponse = await this.getUnauthenticatedFetcher().post([this.tokenEndpoint, 'refresh'], {
                body: {
                    refreshToken: currentTokens.tokens.refreshToken,
                },
                parser: parserFromDto(TokensResponseDto),
            });
            await this.handleRefreshedTokens(TokenPair.fromResponseDto(tokensResponse));
        }
        catch (error) {
            if (error instanceof UnauthorizedError) {
                await this.handleUnauthorizedError(currentTokens);
            }
            else if (error instanceof NetworkConnectionError ||
                error instanceof HttpServerError) {
                if (retries >= MAX_RETRIES) {
                    throw error;
                }
                await resolveAfter(TOKEN_REFRESH_DELAY_MS * Math.pow(2, retries));
                await this.doRefreshToken(retries + 1);
            }
            else {
                throw error;
            }
        }
    }
    async handleRefreshedTokens(tokens) {
        await this.storeTokens(tokens);
        this.resolveWithTokens(tokens);
    }
    async handleUnauthorizedError(tokensPairBeforeRefresh) {
        for (let i = 0; i < MAX_RETRIES; i++) {
            await resolveAfter(i && TOKEN_REFRESH_DELAY_MS);
            const currentTokenPair = await this.getCurrentTokenPair();
            if (!currentTokenPair) {
                return;
            }
            if (tokensPairBeforeRefresh.tokens.refreshToken !==
                currentTokenPair.tokens.refreshToken) {
                this.resolveWithTokens(currentTokenPair);
                return;
            }
        }
        await this.rejectWithInvalidatedToken();
    }
    isTokenRefreshInProgress() {
        return !this.freshTokenResolver.isCompleted;
    }
}
const SERVICE_ACCOUNT_TOKEN_KEY = '$SERVICE_ACCOUNT_TOKEN';
export class ServiceAccountTokenManager {
    token;
    constructor(token) {
        this.token = token;
    }
    static async create(token, tokenStorage) {
        await tokenStorage.put(SERVICE_ACCOUNT_TOKEN_KEY, token);
        return new ServiceAccountTokenManager(token);
    }
    static async tryCreateFromStorage(tokenStorage) {
        const token = await tokenStorage.get(SERVICE_ACCOUNT_TOKEN_KEY);
        if (isNil(token)) {
            return null;
        }
        return new ServiceAccountTokenManager(token);
    }
    getFreshToken() {
        return Promise.resolve(getServiceAccountWebServiceToken(this.token));
    }
    invalidateToken() {
        return Promise.resolve();
    }
    storeTokens() {
        return Promise.resolve();
    }
}
const TOKEN_REFRESH_DELAY_MS = 10;
const MAX_RETRIES = 10;
